import { useEffect, useState } from "react";
import type { VenueOperatingHoursEntry } from "../../lib/api";
import { useAppPreferences } from "../../lib/preferences";
import { getDayLabel, getVenueOpenState } from "./availability";

function normalizeHours(hours: VenueOperatingHoursEntry[]) {
  return [...hours].sort((left, right) => left.day_of_week - right.day_of_week);
}

export function AvailabilityCard({
  disabled = false,
  disabledHint,
  isSubmitting,
  onSubmit,
  timezone,
  value,
}: {
  disabled?: boolean;
  disabledHint?: string;
  isSubmitting: boolean;
  onSubmit: (operatingHours: VenueOperatingHoursEntry[]) => Promise<void>;
  timezone: string;
  value: VenueOperatingHoursEntry[];
}) {
  const { t } = useAppPreferences();
  const [hours, setHours] = useState<VenueOperatingHoursEntry[]>(() => normalizeHours(value));

  useEffect(() => {
    setHours(normalizeHours(value));
  }, [value]);

  const openState = getVenueOpenState(hours, timezone, t);

  const updateEntry = (
    dayOfWeek: number,
    updater: (entry: VenueOperatingHoursEntry) => VenueOperatingHoursEntry,
  ) => {
    setHours((current) =>
      current.map((entry) =>
        entry.day_of_week === dayOfWeek ? updater(entry) : entry,
      ),
    );
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    await onSubmit(hours);
  };

  return (
    <form
      className="ops-surface"
      onSubmit={(event) => void handleSubmit(event)}
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="ops-kicker">{t("Availability")}</p>
        </div>
        <div className="ops-chip" data-tone={openState.isOpenNow ? "success" : "warning"}>
          {openState.isOpenNow ? t("Open now") : t("Closed now")}
        </div>
      </div>

      <p className="mt-2 text-xs leading-4 text-slate-400">
        {openState.reason} · {timezone}
      </p>

      <div className="mt-3 grid gap-2">
        {hours.map((entry) => (
          <div className="ops-record ops-record-dense" key={entry.day_of_week}>
            <div className="flex flex-wrap items-center gap-3">
              <label className="flex min-w-[6rem] flex-1 items-center gap-2 text-sm font-medium text-white">
                <input
                  checked={entry.is_open}
                  className="size-4 rounded border-white/20 bg-slate-900 text-cyan-300"
                  disabled={disabled}
                  onChange={(event) =>
                    updateEntry(entry.day_of_week, (current) => ({
                      ...current,
                      is_open: event.target.checked,
                      open_time: event.target.checked
                        ? current.open_time ?? "10:00"
                        : null,
                      close_time: event.target.checked
                        ? current.close_time ?? "23:00"
                        : null,
                    }))
                  }
                  type="checkbox"
                />
                {t(getDayLabel(entry.day_of_week))}
              </label>
              <div className="flex flex-1 items-end gap-2">
                <label className="ops-field min-w-[5.5rem] flex-1">
                  <span className="ops-label">{t("Opens")}</span>
                  <input
                    className="ops-input"
                    disabled={disabled || !entry.is_open}
                    onChange={(event) =>
                      updateEntry(entry.day_of_week, (current) => ({
                        ...current,
                        open_time: event.target.value,
                      }))
                    }
                    required={entry.is_open}
                    type="time"
                    value={entry.open_time ?? ""}
                  />
                </label>
                <label className="ops-field min-w-[5.5rem] flex-1">
                  <span className="ops-label">{t("Closes")}</span>
                  <input
                    className="ops-input"
                    disabled={disabled || !entry.is_open}
                    onChange={(event) =>
                      updateEntry(entry.day_of_week, (current) => ({
                        ...current,
                        close_time: event.target.value,
                      }))
                    }
                    required={entry.is_open}
                    type="time"
                    value={entry.close_time ?? ""}
                  />
                  {entry.is_open && entry.open_time && entry.close_time && entry.close_time < entry.open_time ? (
                    <span className="mt-1 block text-[0.68rem] font-semibold text-amber-200">{t("Closes next day")}</span>
                  ) : null}
                </label>
              </div>
            </div>
          </div>
        ))}
      </div>

      <span className="mt-4 block" title={disabled ? disabledHint : undefined}>
        <button
          className="ops-button w-full"
          data-size="sm"
          data-variant="primary"
          disabled={disabled || isSubmitting}
          type="submit"
        >
          {isSubmitting ? t("Saving availability...") : t("Save availability")}
        </button>
      </span>
    </form>
  );
}
